import { Link } from "react-router-dom";
import {
  TrendingUp,
  Shield,
  PoundSterling,
  Calculator,
  ArrowRight,
  ChevronRight,
  Zap,
  BarChart3,
  Wallet,
  Clock,
  PieChart,
  Flame,
} from "lucide-react";

const TOOLS = [
  {
    icon: PieChart,
    title: "Budget Calculator",
    desc: "Track income, expenses, savings & debts with our comprehensive budgeting dashboard.",
    href: "/tools/budget-calculator",
    tag: "Budgeting",
    color: "violet",
    testId: "tool-budget-calculator",
  },
  {
    icon: Flame,
    title: "FIRE Calculator",
    desc: "Calculate your path to Financial Independence, Retire Early.",
    href: "/tools/fire-calculator",
    tag: "Retirement",
    color: "orange",
    testId: "tool-fire-calculator",
  },
  {
    icon: TrendingUp,
    title: "Compound Interest",
    desc: "Visualise your path to £1M with powerful projection tools.",
    href: "/tools/compound-interest",
    tag: "Investing",
    color: "emerald",
    testId: "tool-compound-interest",
  },
  {
    icon: PoundSterling,
    title: "UK Take Home Pay",
    desc: "See exactly what hits your bank after Income Tax & NI.",
    href: "/tools/salary-calculator",
    tag: "Salary",
    color: "sky",
    testId: "tool-salary-calculator",
  },
  {
    icon: Shield,
    title: "ISA vs GIA",
    desc: "Compare tax-efficient ISA wrappers vs General Investment Accounts.",
    href: "/tools/isa-calculator",
    tag: "Tax",
    color: "violet",
    testId: "tool-isa-calculator",
  },
  {
    icon: Clock,
    title: "Cost of Waiting",
    desc: "See exactly how much procrastination is costing your retirement.",
    href: "/tools/cost-of-waiting",
    tag: "Wake-Up Call",
    color: "red",
    testId: "tool-cost-of-waiting",
  },
];

const FEATURES = [
  {
    icon: Shield,
    title: "Tax Efficiency",
    desc: "Optimise your ISAs, SIPPs and tax wrappers. Keep more of what you earn in the UK tax system.",
  },
  {
    icon: TrendingUp,
    title: "Compound Growth",
    desc: "Visualise your path to financial independence. See the real power of compounding over time.",
  },
  {
    icon: PoundSterling,
    title: "Salary Breakdown",
    desc: "See exactly what hits your bank account after Income Tax, National Insurance, and pension deductions.",
  },
];

const COLOR_MAP = {
  emerald: {
    bg: "bg-emerald-500/10",
    text: "text-emerald-400",
    border: "border-emerald-500/20",
    tag: "bg-emerald-500/10 text-emerald-400",
    hover: "hover:border-emerald-500/40",
  },
  sky: {
    bg: "bg-sky-500/10",
    text: "text-sky-400",
    border: "border-sky-500/20",
    tag: "bg-sky-500/10 text-sky-400",
    hover: "hover:border-sky-500/40",
  },
  violet: {
    bg: "bg-violet-500/10",
    text: "text-violet-400",
    border: "border-violet-500/20",
    tag: "bg-violet-500/10 text-violet-400",
    hover: "hover:border-violet-500/40",
  },
  amber: {
    bg: "bg-amber-500/10",
    text: "text-amber-400",
    border: "border-amber-500/20",
    tag: "bg-amber-500/10 text-amber-400",
    hover: "hover:border-amber-500/40",
  },
  red: {
    bg: "bg-red-500/10",
    text: "text-red-400",
    border: "border-red-500/20",
    tag: "bg-red-500/10 text-red-400",
    hover: "hover:border-red-500/40",
  },
  orange: {
    bg: "bg-orange-500/10",
    text: "text-orange-400",
    border: "border-orange-500/20",
    tag: "bg-orange-500/10 text-orange-400",
    hover: "hover:border-orange-500/40",
  },
};

export default function Home() {
  return (
    <div className="bg-slate-950">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
      >
        {/* Background glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(16, 185, 129, 0.15), transparent)",
          }}
        />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-sm font-medium">Built for UK Investors</span>
            </div>

            {/* Headline */}
            <h1
              data-testid="hero-headline"
              className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-4"
            >
              Personal Finance Tools
            </h1>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-400 mb-8">
              By <span className="text-emerald-400">Krish Kara</span>
            </h2>

            {/* Subheadline */}
            <p
              data-testid="hero-subheadline"
              className="text-slate-400 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl"
            >
              The ultimate free toolkit for UK investors. Calculate your take-home pay, project ISA
              growth, and build your path to financial freedom — with tools made for the British tax system.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/tools"
                data-testid="hero-cta-explore"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-8 py-3.5 transition-all duration-200 hover:shadow-[0_0_24px_rgba(16,185,129,0.3)] hover:-translate-y-0.5"
              >
                Explore Tools
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://www.krishkara.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-newsletter"
                className="inline-flex items-center justify-center gap-2 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-white font-medium rounded-full px-8 py-3.5 transition-all duration-200 hover:-translate-y-0.5"
              >
                Newsletter
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>

            {/* Social proof */}
            <div className="mt-12 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-500" />
                <span>Built for UK investors</span>
              </div>
              <div className="w-px h-4 bg-slate-800" />
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>HMRC tax rules included</span>
              </div>
              <div className="w-px h-4 bg-slate-800" />
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-500" />
                <span>UK tax rates 2025/26</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section data-testid="tools-grid-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-emerald-400 text-sm font-medium uppercase tracking-widest mb-3">The Toolkit</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything you need to <br className="hidden sm:block" />
              <span className="text-slate-400">take control of your finances</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {TOOLS.map((tool) => {
              const colors = COLOR_MAP[tool.color];
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.href}
                  to={tool.href}
                  data-testid={tool.testId}
                  className={`group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 ${colors.hover} rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30`}
                >
                  <div className={`w-11 h-11 rounded-xl ${colors.bg} flex items-center justify-center mb-5`}>
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <span className={`inline-flex text-xs font-medium rounded-full px-2.5 py-0.5 mb-3 ${colors.tag}`}>
                    {tool.tag}
                  </span>
                  <h3 className="font-outfit font-semibold text-white text-lg mb-2">{tool.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{tool.desc}</p>
                  <div className={`mt-5 flex items-center gap-1 text-xs font-medium ${colors.text}`}>
                    Open Calculator
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="py-20 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-emerald-400 text-sm font-medium uppercase tracking-widest mb-3">Why Kara Finance</p>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
                Built for the British
                <br />
                <span className="text-slate-400">tax & investing system</span>
              </h2>
              <p className="text-slate-400 text-base leading-relaxed mb-8">
                Forget generic US-centric tools. Kara Finance is built from the ground up for UK investors — with ISAs, SIPPs, National Insurance, and HMRC thresholds baked in.
              </p>
              <Link
                to="/tools"
                className="inline-flex items-center gap-2 text-emerald-400 font-medium text-sm hover:text-emerald-300 transition-colors"
              >
                Start calculating <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {FEATURES.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={i}
                    className="flex gap-4 bg-slate-900/30 border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors duration-200"
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-outfit font-semibold text-white mb-1">{feature.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section data-testid="cta-banner" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative rounded-2xl border border-emerald-500/20 p-10 md:p-16 overflow-hidden"
            style={{
              background: "radial-gradient(ellipse 80% 100% at 50% 120%, rgba(16,185,129,0.08), transparent)",
            }}
          >
            <div className="absolute inset-0 bg-slate-900/50 rounded-2xl" />
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to start stacking?
              </h2>
              <p className="text-slate-400 mb-8">
                Dive into the tools, sharpen your strategy, and build wealth the smart way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/tools"
                  data-testid="bottom-cta-explore"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-8 py-3.5 transition-all duration-200 hover:shadow-[0_0_24px_rgba(16,185,129,0.3)] hover:-translate-y-0.5"
                >
                  Explore All Tools <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="https://www.krishkara.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bottom-cta-newsletter"
                  className="inline-flex items-center justify-center gap-2 border border-slate-700 text-slate-300 hover:text-white hover:border-slate-600 font-medium rounded-full px-8 py-3.5 transition-all duration-200"
                >
                  Join Newsletter
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
